import { useState, useCallback, useEffect } from 'react';
import { Head, Link } from '@inertiajs/react';
import { MainLayout } from '../../layouts/MainLayout';
import { PageHeader } from '../../components/PageHeader';
import { AccountSummaryCard } from '../../components/account-receivable/AccountSummaryCard';
import { TransactionList } from '../../components/account-receivable/TransactionList';
import { RegisterPaymentModal } from '../../components/account-receivable/RegisterPaymentModal';
import { getCSRFToken } from '../../utils/csrf';
import { route } from '../../utils/route';
import type { Account, Transaction, Pagination, Labels } from '../../types/accountReceivable';

interface AccountReceivableProps {
  account: Account;
  hasMultipleAccounts: boolean;
  transactions: Transaction[];
  pagination: Pagination;
  labels: Labels;
}

export default function AccountReceivable({
  account,
  hasMultipleAccounts,
  transactions: initialTransactions,
  pagination: initialPagination,
  labels,
}: AccountReceivableProps) {
  const [transactions, setTransactions] = useState<Transaction[]>(initialTransactions);
  const [pagination, setPagination] = useState<Pagination>(initialPagination);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showRegisterPayment, setShowRegisterPayment] = useState(false);

  // Sync state when Inertia refreshes props (e.g. after registering a payment)
  useEffect(() => {
    setTransactions(initialTransactions);
    setPagination(initialPagination);
  }, [initialTransactions, initialPagination]);

  const loadMore = useCallback(async () => {
    if (loading || !pagination.has_more) return;

    setLoading(true);
    setError(null);

    try {
      const nextPage = pagination.current_page + 1;
      const response = await fetch(`${route('api.account.transactions')}?ar_id=${account.id}&page=${nextPage}`, {
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'X-XSRF-TOKEN': getCSRFToken(),
        },
      });

      if (!response.ok) throw new Error('Error loading transactions');

      const data = await response.json();
      setTransactions(prev => [...prev, ...data.transactions]);
      setPagination(data.pagination);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      setLoading(false);
    }
  }, [loading, pagination, account.id]);

  return (
    <MainLayout>
      <Head>
        <title>{`${account.name} - Cuenta Corriente`}</title>
        <meta name="robots" content="noindex" />
      </Head>

      <div className="mx-auto max-w-6xl px-4 py-6">
        {hasMultipleAccounts && (
          <Link href="/cuenta-corriente" className="mb-4 inline-flex items-center text-sm text-gray-500 hover:text-gray-700 no-underline">
            <i className="fa fa-arrow-left mr-1.5" /> Todas las cuentas
          </Link>
        )}

        <div className="flex items-center justify-between">
          <PageHeader title={account.name} subtitle="Consultá tu saldo, movimientos y estado de cuenta." />
          {account.balance > 0 && (
            <button
              onClick={() => setShowRegisterPayment(true)}
              className="rounded-lg bg-green-500 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-green-600"
            >
              Registrar pago
            </button>
          )}
        </div>

        <AccountSummaryCard account={account} labels={labels} />

        <section className="mt-8">
          <h2 className="mb-4 text-lg font-semibold text-gray-800">{labels.transactions}</h2>
          <TransactionList
            transactions={transactions}
            labels={labels}
            loading={loading}
            hasMore={pagination.has_more}
            onLoadMore={loadMore}
          />
          {error && (
            <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}
        </section>
        {showRegisterPayment && (
          <RegisterPaymentModal
            accountId={account.id}
            onClose={() => setShowRegisterPayment(false)}
          />
        )}
      </div>
    </MainLayout>
  );
}
